import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { TripCalculation, SEGMENT_COLORS } from "_interfaces/driverTripCalculator.interfaces";
import {
  formatCurrency,
  formatDistance,
  formatTime,
  formatSpeed,
} from "_services/modules/driverTripCalculator/calculateTrip";
import { useAppTranslation } from "locale/useAppTranslation";

export interface PDFPreviewModalHandle {
  download: () => Promise<void>;
}

interface PDFPreviewModalProps {
  open: boolean;
  onClose: () => void;
  calculationResult: TripCalculation;
}

const PDFPreviewModal = React.forwardRef<PDFPreviewModalHandle, PDFPreviewModalProps>(
  ({ open, onClose, calculationResult }, ref) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const printRef = useRef<HTMLDivElement>(null);
  const { t } = useAppTranslation("driverTripCalculator");
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open) {
      dialog.showModal();
    } else {
      dialog.close();
    }
  }, [open]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === dialogRef.current) onClose();
  };

  const handleDownload = async () => {
    if (!printRef.current) return;
    setIsDownloading(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");

      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      let yOffset = 0;
      let remainingHeight = imgHeight;

      while (remainingHeight > 0) {
        pdf.addImage(imgData, "PNG", 0, -yOffset, imgWidth, imgHeight);
        remainingHeight -= pdfHeight;
        yOffset += pdfHeight;
        if (remainingHeight > 0) pdf.addPage();
      }

      pdf.save(`driver-trip-${calculationResult.id}.pdf`);
    } finally {
      setIsDownloading(false);
    }
  };

  useImperativeHandle(ref, () => ({ download: handleDownload }));

  const calculatedAt = new Date(calculationResult.created_at).toLocaleString("id-ID", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const renderInvoiceContent = () => (
    <div className="bg-white font-sans text-gray-900 text-sm p-8">

          {/* Zone A — Invoice Header */}
          <div className="flex justify-between items-start pb-6 mb-6 border-b-2 border-gray-900">
            {/* Left: document identity */}
            <div className="space-y-1.5">
              <p className="text-2xl font-bold tracking-wide text-gray-900">{t("pdf-invoice-title")}</p>
              <div className="flex items-baseline gap-2">
                <span className="text-sm text-gray-500">{t("pdf-label-date")}</span>
                <span className="text-base font-semibold text-gray-800">{calculatedAt}</span>
              </div>
            </div>
            {/* Right: BILL TO */}
            <div className="text-right">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1.5">{t("pdf-label-bill-to")}</p>
              <p className="text-base font-bold text-gray-900">{calculationResult.customer}</p>
              <p className="text-sm text-gray-600">{calculationResult.plate_number}</p>
              <p className="text-sm text-gray-600">{calculationResult.vehicle_type.name} · {calculationResult.vehicle_model.name}</p>
              <p className="text-sm text-gray-600">{calculationResult.temperature}°C</p>
            </div>
          </div>

          {/* Zone B — Per-Segment Blocks */}
          {calculationResult.segments.map((segment, index) => {
            const segmentColor = SEGMENT_COLORS[index % SEGMENT_COLORS.length];
            const hasFreezerSurcharge = segment.total_freezer_fuel > 0;
            const totalIdle = segment.rest_time + segment.idle_time;
            const fuelLiters = segment.fuel_usage > 0 ? segment.distance_km / segment.fuel_usage : 0;
            const costTypeLabel = (type: string): string => {
              if (type === "toll") return t("slip-cost-toll");
              if (type === "parking") return t("slip-cost-parking");
              if (type === "loading") return t("slip-cost-loading");
              if (type === "other") return t("slip-cost-other");
              return type;
            };
            const hasTollOnRoute = segment.locations.some((loc, i) => i > 0 && loc.avoid_toll === false);

            return (
              <div key={segment.id ?? index}>
                {/* Segment header */}
                <div className="border-l-4 pl-3 mb-4 mt-2" style={{ borderLeftColor: segmentColor }}>
                  <p className="text-sm font-bold uppercase tracking-widest" style={{ color: segmentColor }}>
                    {t("slip-segment-title", { number: index + 1 })}
                  </p>
                  <p className="text-sm text-gray-500">{segment.trip_type?.name ?? t("pdf-unknown-trip-type")}</p>
                </div>

                {/* Route Locations */}
                <div className="mb-3 space-y-1">
                  {(() => {
                    const locationPoints = segment.locations.length > 0
                      ? [
                          { name: segment.locations[0].start_location_name, key: `start-${segment.locations[0].id || 0}`, hasToll: false },
                          ...segment.locations.map((loc, i) => ({
                            name: loc.end_location_name,
                            key: `end-${loc.id || i}`,
                            hasToll: loc.avoid_toll === false,
                          })),
                        ]
                      : [];

                    return locationPoints.map((point, ptIdx) => {
                      const isFirst = ptIdx === 0;
                      const isLast = ptIdx === locationPoints.length - 1;
                      return (
                        <div key={point.key} className="flex items-start gap-2 text-sm">
                          <div className="mt-1 flex flex-col items-center">
                            <div className={`h-2.5 w-2.5 rounded-full ${isFirst ? "bg-primary-500" : isLast ? "bg-red-500" : "border-2 border-gray-400"}`} />
                            {!isLast && <div className="mt-0.5 h-4 w-0.5 bg-gray-300" />}
                          </div>
                          <div className="flex-1">
                            <span className="text-gray-700">{point.name}</span>
                            {point.hasToll && !isFirst && (
                              <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{t("slip-label-toll")}</span>
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>

                {/* Distance & Time */}
                <div className="flex items-center gap-1.5 text-sm text-gray-700 mb-4 flex-wrap">
                  <span className="font-medium">{formatDistance(segment.distance_km)}</span>
                  <span className="text-gray-400">|</span>
                  <span className="text-gray-600">{segment.avg_speed} {t("unit-km")}/{t("unit-hours")}</span>
                  <span className="text-gray-400">|</span>
                  <span className="text-gray-600">{formatTime(segment.recommended_time_minutes, t)}</span>
                  {hasTollOnRoute && (
                    <span className="bg-amber-100 text-amber-700 text-xs px-1.5 py-0.5 rounded-full leading-none ml-1">
                      {t("slip-label-toll")}
                    </span>
                  )}
                </div>

                {/* System Recommendation row */}
                <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3 -mt-2 flex-wrap">
                  <span className="font-semibold text-blue-600 uppercase tracking-wide">
                    {t("slip-label-system-recommendation")}
                  </span>
                  <span className="text-gray-400">|</span>
                  <span>{t("slip-label-rest-time")} {formatTime(segment.rest_time * 60, t)}</span>
                  <span className="text-gray-400">|</span>
                  <span>{formatTime(segment.recommended_time_minutes + segment.rest_time * 60, t)}</span>
                  <span className="text-gray-400">|</span>
                  <span>{t("modal-pdf-trip-days")} = {segment.trip_days} {t("modal-pdf-days")}</span>
                </div>

                {/* Line-item table */}
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-gray-300">
                      <th className="text-left pb-1.5 text-xs font-bold uppercase tracking-widest text-gray-400 pr-4">{t("pdf-col-description")}</th>
                      <th className="text-right pb-1.5 text-xs font-bold uppercase tracking-widest text-gray-400 pl-4 whitespace-nowrap">{t("pdf-col-qty")}</th>
                      <th className="text-right pb-1.5 text-xs font-bold uppercase tracking-widest text-gray-400 pl-4 whitespace-nowrap">{t("pdf-col-rate")}</th>
                      <th className="text-right pb-1.5 text-xs font-bold uppercase tracking-widest text-gray-400 pl-4 whitespace-nowrap">{t("pdf-col-amount")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Basic Rate */}
                    <tr className="border-b border-gray-100">
                      <td className="py-1.5 text-gray-700 pr-4">{t("modal-pdf-basic-rate")}</td>
                      <td className="py-1.5 text-right text-gray-600 tabular-nums pl-4 whitespace-nowrap">{segment.trip_days} {t("modal-pdf-days")}</td>
                      <td className="py-1.5 text-right text-gray-600 tabular-nums pl-4 whitespace-nowrap">{formatCurrency(segment.driver_basic_rate)}/{t("unit-per-day")}</td>
                      <td className="py-1.5 text-right font-medium tabular-nums pl-4 whitespace-nowrap">{formatCurrency(segment.total_basic_rate)}</td>
                    </tr>
                    {/* Incentive */}
                    <tr className="border-b border-gray-100">
                      <td className="py-1.5 text-gray-700 pr-4">{t("modal-pdf-incentive-amount")}</td>
                      <td className="py-1.5 text-right text-gray-600 tabular-nums pl-4 whitespace-nowrap">{segment.distance_km} {t("unit-km")}</td>
                      <td className="py-1.5 text-right text-gray-600 tabular-nums pl-4 whitespace-nowrap">{formatCurrency(segment.driver_insentif_rate)}/{t("unit-km")}</td>
                      <td className="py-1.5 text-right font-medium tabular-nums pl-4 whitespace-nowrap">{formatCurrency(segment.incentive_rate)}</td>
                    </tr>
                    {/* Trip Fuel */}
                    <tr className="border-b border-gray-100">
                      <td className="py-1.5 text-gray-700 pr-4">{t("modal-pdf-solar-perjalanan")}</td>
                      <td className="py-1.5 text-right text-gray-600 tabular-nums pl-4 whitespace-nowrap">{fuelLiters.toFixed(1)} {t("unit-liter")}</td>
                      <td className="py-1.5 text-right text-gray-600 tabular-nums pl-4 whitespace-nowrap">{formatCurrency(segment.fuel_price)}/{t("unit-liter")}</td>
                      <td className="py-1.5 text-right font-medium tabular-nums pl-4 whitespace-nowrap">{formatCurrency(segment.solar_perjalanan)}</td>
                    </tr>
                    {/* Freezer Fuel Surcharge (conditional) */}
                    {hasFreezerSurcharge && (
                      <tr className="border-b border-gray-100">
                        <td className="py-1.5 text-gray-700 pr-4">{t("modal-pdf-freezer-fuel-total")}</td>
                        <td className="py-1.5 text-right text-gray-600 tabular-nums pl-4 whitespace-nowrap">{totalIdle} {t("unit-hours")}</td>
                        <td className="py-1.5 text-right text-gray-600 tabular-nums pl-4 whitespace-nowrap">{segment.freezer_fuel_efficiency} {t("unit-liter")}/{t("unit-hours")} × {formatCurrency(segment.fuel_price)}/{t("unit-liter")}</td>
                        <td className="py-1.5 text-right font-medium tabular-nums pl-4 whitespace-nowrap">{formatCurrency(segment.total_freezer_fuel)}</td>
                      </tr>
                    )}
                    {/* Additional costs */}
                    {segment.additional_costs.map((cost, cIdx) => (
                      <tr key={cost.id ?? cIdx} className="border-b border-gray-100">
                        <td className="py-1.5 text-gray-700 pr-4">{costTypeLabel(cost.cost_type)}</td>
                        <td className="py-1.5 text-right text-gray-400 pl-4">—</td>
                        <td className="py-1.5 text-right text-gray-400 pl-4">—</td>
                        <td className="py-1.5 text-right font-medium tabular-nums pl-4 whitespace-nowrap">{formatCurrency(cost.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-100">
                      <td colSpan={3} className="py-2 pl-2 text-xs font-bold uppercase tracking-wide text-gray-700">
                        {t("slip-segment-title", { number: index + 1 })} {t("pdf-label-subtotal")}
                      </td>
                      <td className="py-2 pr-2 text-right font-bold tabular-nums text-gray-900 whitespace-nowrap">
                        {formatCurrency(segment.segment_total)}
                      </td>
                    </tr>
                  </tfoot>
                </table>

                {index < calculationResult.segments.length - 1 && (
                  <hr className="border-gray-200 mt-5 mb-1" />
                )}
              </div>
            );
          })}

          {/* Zone C — Summary + Grand Total */}
          <div className="mt-6 pt-4 border-t border-gray-300">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">{t("pdf-label-summary")}</p>
            {calculationResult.segments.map((segment, index) => (
              <div key={segment.id ?? index} className="flex justify-between text-sm py-1 border-b border-gray-100">
                <span className="text-gray-600">
                  {t("slip-segment-title", { number: index + 1 })}
                  {segment.trip_type?.name ? ` — ${segment.trip_type.name}` : ""}
                </span>
                <span className="font-medium tabular-nums">{formatCurrency(segment.segment_total)}</span>
              </div>
            ))}
          </div>

          {/* Grand Total */}
          <div className="border-t border-gray-900 mt-1" />
          <div className="border-t border-gray-900 mb-0" />
          <div className="flex justify-between items-baseline pt-3">
            <span className="text-base font-bold uppercase tracking-widest text-gray-900">{t("slip-label-grand-total")}</span>
            <span className="text-2xl font-bold tabular-nums text-gray-900">{formatCurrency(calculationResult.grand_total)}</span>
          </div>
        </div>
  );

  return (
    <>
      {/* Off-screen container for html2canvas — always mounted, never hidden */}
      <div
        ref={printRef}
        style={{ position: "fixed", left: "-9999px", top: 0, width: "794px", zIndex: -1 }}
        aria-hidden="true"
      >
        {renderInvoiceContent()}
      </div>

      <dialog
        ref={dialogRef}
        className="rounded-2xl border border-border bg-white shadow-xl backdrop:bg-black/60 w-full max-w-3xl max-h-[90vh] p-0 overflow-hidden"
        onClick={handleBackdropClick}
        onClose={onClose}
      >
        <div className="flex flex-col max-h-[90vh]">
          {/* Modal Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 flex-shrink-0">
            <h2 className="text-lg font-bold text-gray-900">{t("modal-pdf-title")}</h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              aria-label="Close"
              data-testid="btn-dtc-pdf-close"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Scrollable preview area */}
          <div className="flex-1 overflow-y-auto p-6">
            {renderInvoiceContent()}
          </div>

          {/* Footer buttons */}
          <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              data-testid="btn-dtc-pdf-cancel"
            >
              {t("btn-cancel")}
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={isDownloading}
              className="rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-600 transition-colors disabled:opacity-60"
              data-testid="btn-dtc-pdf-download"
            >
              {isDownloading ? t("modal-pdf-downloading") : t("btn-download-pdf")}
            </button>
          </div>
        </div>
      </dialog>
    </>
  );
});

PDFPreviewModal.displayName = "PDFPreviewModal";

export default PDFPreviewModal;
